import { CONFIG_ATTRIBUTE_BOOKING_URL, CONFIG_ATTRIBUTE_LAUNCHER, TRIGGER_ATTRIBUTE } from "./constants";
import { buildLauncher, mountLauncher, setLauncherHidden } from "./dom";
import { BrothersReservationsWidget } from "./widget";
import type { WidgetConfigInput } from "./types";

export interface BrothersReservationsApi {
  open(): void;
  close(): void;
  isOpen(): boolean;
  configure(config: WidgetConfigInput): void;
}

declare global {
  interface Window {
    BrothersReservations?: BrothersReservationsApi;
  }
}

/** Section 3: the ONE global this script creates — a single, stable namespace, never scattering multiple globals across window. */
function findOwnScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) return current;
  // Fallback for the rare case currentScript is unavailable (e.g. a
  // dynamically-injected <script> without defer/async handled specially) —
  // matches by filename rather than a full URL, so it still works
  // regardless of which host/path served it.
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
  return scripts.find((s) => /reservation-widget(\.min)?\.js/.test(s.src)) ?? null;
}

/** Section 25: zero-config default — the booking app is assumed to be served from this very script's own origin (same deployment), overridable via data-booking-url for local/staging testing. */
function resolveDefaultBookingUrl(ownScript: HTMLScriptElement | null): string {
  const explicit = ownScript?.getAttribute(CONFIG_ATTRIBUTE_BOOKING_URL);
  if (explicit) return explicit;
  const origin = ownScript ? new URL(ownScript.src).origin : window.location.origin;
  return `${origin}/`;
}

function init(): void {
  // Section 3: loading this script twice (a Wix editor duplicate-paste,
  // a second Custom Code block) must never create a second modal/second
  // set of listeners — reuse the existing instance instead.
  if (window.BrothersReservations) return;

  const ownScript = findOwnScript();
  const widget = new BrothersReservationsWidget({ bookingUrl: resolveDefaultBookingUrl(ownScript) });

  /**
   * Owner product correction 2026-09-02: the launcher is created here, not
   * by the host. `launcher` stays null when the host opted out, so every
   * call below has to tolerate its absence rather than assume it exists.
   */
  const launcherEnabled = ownScript?.getAttribute(CONFIG_ATTRIBUTE_LAUNCHER) !== "false";
  let launcher: HTMLButtonElement | null = null;

  const api: BrothersReservationsApi = {
    open: () => {
      widget.open();
      if (launcher) setLauncherHidden(launcher, true);
    },
    close: () => {
      widget.close();
      if (launcher) setLauncherHidden(launcher, false);
    },
    isOpen: () => widget.isOpen(),
    configure: (config) => widget.configure(config),
  };
  window.BrothersReservations = api;

  if (launcherEnabled) {
    launcher = buildLauncher(() => api.open());
    mountLauncher(launcher);
  }

  /*
   * The modal can also close WITHOUT api.close() — the X button, Escape, or
   * a CLOSE_REQUEST from the iframe all go straight to the widget. Reveal
   * the launcher again on any of those, otherwise a guest who closes with
   * the X would lose the only way back in.
   */
  widget.onClosed(() => {
    if (launcher) setLauncherHidden(launcher, false);
  });

  // Section 24B: a robust, non-Wix-specific declarative trigger — event
  // delegation on `document` so it also matches a trigger element Wix
  // adds to the page after this script has already run.
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest(`[${TRIGGER_ATTRIBUTE}]`);
    if (!trigger) return;
    event.preventDefault();
    api.open();
  });
}

init();
